import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useContext, useState } from 'react';
import { MobileLayout } from '../../components/layouts/MobileLayout';
import {
    useBenchmarkData,
    useFrontierData,
    useOwnershipData
} from '../../hooks/data';
import DesktopLayout from '../../components/layouts/DesktopLayout';
import FrontierSection from '../../components/sections/FrontierSection';
import ChartTab from '../../components/tabs/ChartTab';
import TableTab from '../../components/tabs/TableTab';
import { PortfolioDetails } from '../../components/menus/PortfolioDetailsMobile';
import { setComparisonFilters } from '../../functions/filtering';
import { FilterStatesContext } from '../../components/contexts/filterStates';
import { useFilteredData } from '../../hooks/filtering';
import Grid from '@mui/material/Grid';
import AppPageHeader from '../../components/AppPageHeader';
import Dashboard from '../../components/Dashboard';
import PageSection from '../../components/layouts/PageSection';
import GoProDialog from '../../components/forms/GoProDialog';

/**
 * BasicAppPage component renders the app page layout for "Basic" users.
 * @returns {JSX.Element} The rendered BasicAppPage component.
 */
const BasicAppPage = () => {
    const theme = useTheme();
    const isBelowSm = useMediaQuery(theme.breakpoints.down('sm'));
    const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));

    const [selectedFrontierDot, setSelectedFrontierDot] = useState(null);

    const { benchmarkData } = useBenchmarkData();
    const { ownershipData } = useOwnershipData();
    const { frontierData, ebabPeriod } = useFrontierData(
        benchmarkData,
        ownershipData
    );

    const { peerFilterDispatch, setSearchState } =
        useContext(FilterStatesContext);
    const { filteredData } = useFilteredData(frontierData);

    const [portfolioDetailsDialogOpen, setPortfolioDetailsDialogOpen] =
        useState(false);

    const [goProDialogOpen, setGoProDialogOpen] = useState(false);
    const [mobileTab, setMobileTab] = useState(0);

    /**
     * Handle a Mobile Portfolio Details Vs. Peer Group click
     */
    function handleMobileVsPeerGroupClick() {
        setSearchState(null);
        setComparisonFilters(selectedFrontierDot, peerFilterDispatch);
        setPortfolioDetailsDialogOpen(false);
    }

    if (isBelowSm) {
        return (
            <>
                <MobileLayout
                    mobileTab={mobileTab}
                    setMobileTab={setMobileTab}
                    labels={['Chart', 'Table']}
                >
                    <ChartTab
                        data={filteredData}
                        onClick={(event, data) => {
                            event.stopPropagation();
                            setSelectedFrontierDot(data.datum);
                            setPortfolioDetailsDialogOpen(true);
                        }}
                    />
                    <TableTab
                        data={filteredData}
                        onRowClick={(row) => {
                            setSelectedFrontierDot(row);
                            setPortfolioDetailsDialogOpen(true);
                        }}
                    />
                </MobileLayout>
                {selectedFrontierDot && (
                    <PortfolioDetails
                        portfolioData={selectedFrontierDot}
                        open={portfolioDetailsDialogOpen}
                        handleClose={() => setPortfolioDetailsDialogOpen(false)}
                        onVsPeerGroupClick={handleMobileVsPeerGroupClick}
                        onMoreDetailsClick={() => {
                            setGoProDialogOpen(true);
                        }}
                    />
                )}
                <GoProDialog
                    open={goProDialogOpen}
                    onFormClose={() => {
                        setGoProDialogOpen(false);
                    }}
                    onSubmissionViewClose={() => {
                        setPortfolioDetailsDialogOpen(false);
                        setGoProDialogOpen(false);
                    }}
                />
            </>
        );
    } else {
        return (
            <>
                <DesktopLayout>
                    <PageSection>
                        {/*Typography and filter button for sm and xs*/}
                        <Grid
                            item
                            xs={12}
                            marginTop={{ xs: '24px', sm: '32px' }}
                        >
                            <AppPageHeader ebabPeriod={ebabPeriod} />
                        </Grid>
                        {/*Dashboard Component (md and above) */}
                        {!isBelowMd && (
                            <Grid item md={12} id='appPageDashboard'>
                                <Dashboard />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <FrontierSection
                                data={filteredData}
                                handleMoreDetailsClick={() =>
                                    setGoProDialogOpen(true)
                                }
                                isBasic={true}
                            />
                        </Grid>
                    </PageSection>
                </DesktopLayout>
                <GoProDialog
                    open={goProDialogOpen}
                    onFormClose={() => {
                        setGoProDialogOpen(false);
                    }}
                    onSubmissionViewClose={() => {
                        setGoProDialogOpen(false);
                    }}
                />
            </>
        );
    }
};

export default BasicAppPage;
